import type { CustomBenchmarkImage, ListBenchmarkImagesQueryVariables } from '@vizit/api';
import { useListBenchmarkImagesQuery } from '@vizit/api';
import type { BenchmarkImageItem, BenchmarkImageStatus } from '@vizit/organisms';
import * as React from 'react';
import { benchmarkImagesRefetchInterval } from './use-benchmark-images.polling';

/**
 * Benchmarks hold tens to a few hundred images and the page shows them all, so it asks for one large page instead
 * of paging. The API caps a single add at 500 images.
 */
const BENCHMARK_IMAGES_LIMIT = 500;

enum BenchmarkImageSortOption {
  Newest = 'newest',
  Oldest = 'oldest',
  Name = 'name',
}

const SORT_QUERY: Record<
  BenchmarkImageSortOption,
  Pick<ListBenchmarkImagesQueryVariables, 'sort_by' | 'sort_direction'>
> = {
  [BenchmarkImageSortOption.Newest]: { sort_by: 'created_at', sort_direction: 'desc' },
  [BenchmarkImageSortOption.Oldest]: { sort_by: 'created_at', sort_direction: 'asc' },
  [BenchmarkImageSortOption.Name]: { sort_by: 'name', sort_direction: 'asc' },
};

function parseBenchmarkImageSortOption(value: unknown): BenchmarkImageSortOption {
  return value === BenchmarkImageSortOption.Oldest || value === BenchmarkImageSortOption.Name
    ? value
    : BenchmarkImageSortOption.Newest;
}

/**
 * Where an image is, as the page can tell: the API cannot report that an uploaded file has landed, so processing
 * means its upload is still running in this browser, and failed means that upload did not complete. Anything else
 * the listing returns is processed.
 */
function getBenchmarkImageStatus(
  image: Pick<CustomBenchmarkImage, 'id'>,
  uploads: { uploadingImageIds?: ReadonlySet<string>; failedImageIds?: ReadonlySet<string> } = {},
): BenchmarkImageStatus {
  if (uploads.failedImageIds?.has(image.id)) {
    return 'failed';
  }
  return uploads.uploadingImageIds?.has(image.id) ? 'processing' : 'ready';
}

interface UseBenchmarkImagesProps {
  workspaceId?: string;
  benchmarkId: string;
  sortOption?: BenchmarkImageSortOption;
  /** Images whose upload to storage is still running, from `useBenchmarkImageUpload` */
  uploadingImageIds?: ReadonlySet<string>;
  /** Images whose upload to storage failed, from `useBenchmarkImageUpload` */
  failedImageIds?: ReadonlySet<string>;
}

/**
 * The images of a benchmark for its page: the mapped images, their grid tiles with a status each, how many are
 * still uploading and the total count. Keeps itself fresh while a scoring is pending (see the polling module).
 */
function useBenchmarkImages({
  workspaceId,
  benchmarkId,
  sortOption = BenchmarkImageSortOption.Newest,
  uploadingImageIds,
  failedImageIds,
}: UseBenchmarkImagesProps) {
  const variables: ListBenchmarkImagesQueryVariables = {
    workspace_id: workspaceId ?? '',
    benchmark_id: benchmarkId,
    limit: BENCHMARK_IMAGES_LIMIT,
    ...SORT_QUERY[sortOption],
  };
  const { data, isLoading, isError, refetch } = useListBenchmarkImagesQuery(variables, {
    enabled: !!workspaceId && !!benchmarkId,
    // Re-reads the list while a scoring is pending; see the polling module for when it stops.
    refetchInterval: benchmarkImagesRefetchInterval(variables),
  });

  const images = React.useMemo(() => data?.images ?? [], [data]);

  const items = React.useMemo<BenchmarkImageItem[]>(
    () =>
      images.map((image) => {
        const status = getBenchmarkImageStatus(image, { uploadingImageIds, failedImageIds });
        // Until the file is in storage its URLs only 404, so the tile asks for nothing.
        const hasFile = status === 'ready';
        return {
          id: image.id,
          name: image.name,
          thumbnailUrl: hasFile ? image.thumbnailUrl : null,
          fallbackUrl: hasFile ? image.originalUrl : null,
          status,
        };
      }),
    [images, uploadingImageIds, failedImageIds],
  );

  const processingCount = items.filter((item) => item.status === 'processing').length;

  return {
    images,
    items,
    totalCount: data?.totalCount ?? 0,
    processingCount,
    // A disabled query reports isLoading false, so a workspace that has not resolved yet counts as loading.
    isLoading: isLoading || !workspaceId,
    isError,
    refetch,
  };
}

export { BenchmarkImageSortOption, getBenchmarkImageStatus, parseBenchmarkImageSortOption, useBenchmarkImages };
